/**
 * Log view component - Displays history of check-ins
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { CheckIn } from '../models/check-in.js';
import { databaseService, DatabaseService } from '../services/database.js';

@customElement('log-view')
export class LogView extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .container {
      padding: 0;
    }

    .header {
      margin-bottom: 0.5rem;
      padding: 0.25rem 0.5rem;
      border-bottom: 1px solid var(--color-border);
    }

    .title {
      font-size: 0.85rem;
      font-weight: 400;
      color: var(--text-primary);
      margin-bottom: 0.1rem;
    }

    .subtitle {
      font-size: 0.75rem;
      color: var(--text-secondary);
    }

    .empty-state {
      padding: 1rem 0.5rem;
      color: var(--text-secondary);
    }

    .empty-text {
      font-size: 0.85rem;
      margin-bottom: 0.25rem;
    }

    .empty-hint {
      font-size: 0.75rem;
      color: var(--text-tertiary);
    }

    .load-more {
      display: flex;
      justify-content: center;
      margin-top: 0.5rem;
    }

    .load-more-button {
      padding: 0.25rem 0.5rem;
      background-color: var(--bg-secondary);
      color: var(--text-primary);
      border: 1px solid var(--color-border);
      border-radius: 0;
      cursor: pointer;
      transition: none;
    }

    .load-more-button:hover {
      background-color: var(--bg-tertiary);
    }

    .loading {
      padding: 1rem;
      color: var(--text-secondary);
      font-size: 0.85rem;
    }

    .entry {
      background-color: var(--bg-secondary);
      border-left: 2px solid var(--color-border);
      padding: 0.5rem;
      margin-bottom: 0.25rem;
    }

    .entry-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 0.25rem;
      font-size: 0.75rem;
      color: var(--text-tertiary);
    }

    .timestamp {
      color: var(--color-primary);
    }

    .duration {
      color: var(--text-tertiary);
    }

    .section {
      margin-bottom: 0.5rem;
    }

    .section:last-child {
      margin-bottom: 0;
    }

    .section-label {
      font-size: 0.75rem;
      color: var(--text-secondary);
      margin-bottom: 0.1rem;
    }

    .section-content {
      font-size: 0.85rem;
      color: var(--text-primary);
      white-space: pre-wrap;
      line-height: 1.4;
    }

    .attachments {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      margin-top: 0.25rem;
    }

    .attachment-photo {
      width: 60px;
      height: 60px;
      object-fit: cover;
      cursor: pointer;
    }

    .attachment-audio {
      height: 24px;
    }

    .photo-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      cursor: pointer;
    }

    .photo-overlay img {
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
    }
  `;

  @state() private checkIns: CheckIn[] = [];
  @state() private loading = true;
  @state() private hasMore = false;
  @state() private attachmentUrls = new Map<string, Map<string, string>>();
  @state() private expandedPhoto: string | null = null;
  private limit = 50;
  private skip = 0;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadCheckIns();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupUrls();
  }

  private cleanupUrls() {
    for (const entryMap of this.attachmentUrls.values()) {
      for (const url of entryMap.values()) {
        URL.revokeObjectURL(url);
      }
    }
    this.attachmentUrls.clear();
  }

  private async loadCheckIns(loadMore = false) {
    if (loadMore) {
      this.skip += this.limit;
    } else {
      this.skip = 0;
      this.loading = true;
    }

    try {
      const newCheckIns = await databaseService.getRecentCheckIns(this.limit, this.skip);

      if (loadMore) {
        this.checkIns = [...this.checkIns, ...newCheckIns];
      } else {
        this.checkIns = newCheckIns;
      }

      this.hasMore = newCheckIns.length === this.limit;

      // Load attachments for new check-ins
      await this.loadAttachments(newCheckIns);
    } catch (error) {
      console.error('Failed to load check-ins:', error);
    } finally {
      this.loading = false;
    }
  }

  private async loadAttachments(checkIns: CheckIn[]) {
    const updated = new Map(this.attachmentUrls);

    for (const checkIn of checkIns) {
      const refs = DatabaseService.getAttachmentRefs(checkIn);
      if (refs.length === 0) continue;

      const entryMap = new Map<string, string>();
      for (const ref of refs) {
        try {
          const blob = await databaseService.getAttachment(checkIn._id, ref.key);
          if (blob) {
            entryMap.set(ref.key, URL.createObjectURL(blob));
          }
        } catch {
          // Skip failed attachments
        }
      }
      if (entryMap.size > 0) {
        updated.set(checkIn._id, entryMap);
      }
    }

    this.attachmentUrls = updated;
  }

  private async handleLoadMore() {
    await this.loadCheckIns(true);
  }

  private formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    if (isToday) {
      return time;
    }

    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return `${dateStr} ${time}`;
  }

  private formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }

  private renderAttachments(checkIn: CheckIn, step: 'past' | 'future') {
    const refs = DatabaseService.getAttachmentRefs(checkIn).filter(r => r.step === step);
    if (refs.length === 0) return '';

    const entryMap = this.attachmentUrls.get(checkIn._id);
    if (!entryMap) return '';

    const items = refs
      .map(ref => ({ ref, url: entryMap.get(ref.key) }))
      .filter(item => item.url);

    if (items.length === 0) return '';

    return html`
      <div class="attachments">
        ${items.map(({ ref, url }) =>
          ref.mediaType === 'photo'
            ? html`<img class="attachment-photo" src="${url}" alt="Photo" @click=${() => { this.expandedPhoto = url!; }} />`
            : html`<audio class="attachment-audio" src="${url}" controls></audio>`
        )}
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="container">
          <div class="loading">loading...</div>
        </div>
      `;
    }

    if (this.checkIns.length === 0) {
      return html`
        <div class="container">
          <div class="empty-state">
            <div class="empty-text">no check-ins yet</div>
            <div class="empty-hint">complete first check-in to start</div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="container">
        <div class="header">
          <div class="title">journal</div>
          <div class="subtitle">${this.checkIns.length} check-in${this.checkIns.length !== 1 ? 's' : ''}</div>
        </div>

        <div class="entries">
          ${this.checkIns.map((checkIn) => {
            const duration = checkIn.lastCheckInTime
              ? this.formatDuration(checkIn.checkInTime - checkIn.lastCheckInTime)
              : null;

            return html`
              <div class="entry">
                <div class="entry-header">
                  <span class="timestamp">${this.formatTimestamp(checkIn.checkInTime)}</span>
                  ${duration ? html`<span class="duration">${duration}</span>` : ''}
                </div>

                <div class="section">
                  <div class="section-label">what happened:</div>
                  <div class="section-content">${checkIn.pastDescription}</div>
                  ${this.renderAttachments(checkIn, 'past')}
                </div>

                <div class="section">
                  <div class="section-label">what's next:</div>
                  <div class="section-content">${checkIn.futureDescription}</div>
                  ${this.renderAttachments(checkIn, 'future')}
                </div>
              </div>
            `;
          })}
        </div>

        ${this.hasMore ? html`
          <div class="load-more">
            <button class="load-more-button" @click=${this.handleLoadMore}>
              load more
            </button>
          </div>
        ` : ''}
      </div>

      ${this.expandedPhoto ? html`
        <div class="photo-overlay" @click=${() => { this.expandedPhoto = null; }}>
          <img src="${this.expandedPhoto}" alt="Full size photo" />
        </div>
      ` : ''}
    `;
  }
}
